import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDomainConfig } from '../hooks/useDomainConfig';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register' | 'forgot-password' | 'verify-email' | 'reset-password';
  initialEmail?: string;
  resetToken?: string;
}

type AuthMode = 'login' | 'register' | 'forgot-password' | 'verify-email' | 'reset-password' | 'success';

export function AuthModal({ 
  isOpen, 
  onClose, 
  initialMode = 'login',
  initialEmail = '',
  resetToken = ''
}: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationToken, setVerificationToken] = useState('');

  const { 
    login, 
    register, 
    forgotPassword, 
    resetPassword, 
    verifyEmail, 
    resendVerification,
    clearError,
    error: authError 
  } = useAuth();
  
  const domainConfig = useDomainConfig();
  const brandName = domainConfig?.branding?.name || 'SocialDownloader';
  const primaryColor = domainConfig?.branding?.colors?.primary || '#6366F1';

  // Reset form when mode changes
  useEffect(() => {
    setError('');
    setSuccess('');
    setPassword('');
    setConfirmPassword('');
    setVerificationToken('');
    clearError();
    
    if (mode !== 'register') {
      setUsername('');
    }
  }, [mode, clearError]); // Keep clearError in dependency array

  // Handle auth errors
  useEffect(() => {
    if (authError) {
      setError(authError);
      setIsLoading(false);
    }
  }, [authError]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMode(initialMode);
      setEmail(initialEmail);
      setPassword('');
      setConfirmPassword('');
      setUsername('');
      setError('');
      setSuccess('');
      setVerificationToken('');
      setIsLoading(false);
      clearError();
    }
  }, [isOpen, initialMode, initialEmail, clearError]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/\d/.test(password)) return 'Password must contain at least one number';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      switch (mode) {
        case 'login':
          if (!validateEmail(email)) {
            throw new Error('Please enter a valid email address');
          }
          if (!password) {
            throw new Error('Password is required');
          }
          await login(email, password, rememberMe);
          setSuccess('Login successful!');
          setTimeout(() => onClose(), 1000);
          break;

        case 'register':
          if (!validateEmail(email)) {
            throw new Error('Please enter a valid email address');
          }
          const passwordError = validatePassword(password);
          if (passwordError) {
            throw new Error(passwordError);
          }
          if (password !== confirmPassword) {
            throw new Error('Passwords do not match');
          }
          await register(email, password, username || undefined);
          setMode('verify-email');
          setSuccess('Registration successful! Please check your email to verify your account.');
          break;

        case 'forgot-password':
          if (!validateEmail(email)) {
            throw new Error('Please enter a valid email address');
          }
          await forgotPassword(email);
          setSuccess('Password reset instructions have been sent to your email.');
          setTimeout(() => setMode('login'), 3000);
          break;

        case 'reset-password':
          const resetPasswordError = validatePassword(password);
          if (resetPasswordError) {
            throw new Error(resetPasswordError);
          }
          if (password !== confirmPassword) {
            throw new Error('Passwords do not match');
          }
          await resetPassword(resetToken, password);
          setSuccess('Password reset successful! You can now login with your new password.');
          setTimeout(() => setMode('login'), 3000);
          break;

        case 'verify-email':
          if (!verificationToken) {
            throw new Error('Verification token is required');
          }
          await verifyEmail(verificationToken);
          setSuccess('Email verified successfully! You can now login.');
          setTimeout(() => setMode('login'), 3000);
          break;
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setIsLoading(true);
      await resendVerification(email);
      setSuccess('Verification email sent!');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to resend verification email');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTitle = () => {
    switch (mode) {
      case 'login': return `Sign in to ${brandName}`;
      case 'register': return `Create your ${brandName} account`;
      case 'forgot-password': return 'Reset your password';
      case 'reset-password': return 'Set new password';
      case 'verify-email': return 'Verify your email';
      case 'success': return 'Success!';
      default: return `Welcome to ${brandName}`;
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'login': return 'Access your download history and preferences';
      case 'register': return 'Get unlimited downloads and exclusive features';
      case 'forgot-password': return "Enter your email and we'll send you reset instructions";
      case 'reset-password': return 'Enter your new password below';
      case 'verify-email': return 'Enter the verification code sent to your email';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-md p-4 sm:p-6 my-4 sm:my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900" style={{ color: primaryColor }}>
                {getTitle()}
              </h3>
              {getSubtitle() && (
                <p className="mt-1 text-sm text-gray-500">
                  {getSubtitle()}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email field (for most modes) */}
            {(['login', 'register', 'forgot-password'].includes(mode)) && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:border-indigo-500"
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Username field (register only) */}
            {mode === 'register' && (
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  Username (optional)
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:border-indigo-500"
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Password field */}
            {(['login', 'register', 'reset-password'].includes(mode)) && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {mode === 'reset-password' ? 'New Password' : 'Password'}
                </label>
                <div className="relative mt-1">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:border-indigo-500"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M21.536 15.536L8.464 8.464" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {mode === 'register' && (
                  <p className="mt-1 text-xs text-gray-500">
                    At least 8 characters with uppercase, lowercase, and number
                  </p>
                )}
              </div>
            )}

            {/* Confirm Password field */}
            {(['register', 'reset-password'].includes(mode)) && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:border-indigo-500"
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Verification Token field */}
            {mode === 'verify-email' && (
              <div>
                <label htmlFor="verificationToken" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <input
                  id="verificationToken"
                  type="text"
                  value={verificationToken}
                  onChange={(e) => setVerificationToken(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:border-indigo-500"
                  placeholder="Enter verification code"
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Remember Me checkbox (login only) */}
            {mode === 'login' && (
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: primaryColor
              }}
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              {(() => {
                switch (mode) {
                  case 'login': return 'Sign In';
                  case 'register': return 'Create Account';
                  case 'forgot-password': return 'Send Reset Link';
                  case 'reset-password': return 'Reset Password';
                  case 'verify-email': return 'Verify Email';
                  default: return 'Submit';
                }
              })()}
            </button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center">
            {mode === 'login' && (
              <div className="space-y-2">
                <button
                  onClick={() => setMode('forgot-password')}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                  style={{ color: primaryColor }}
                >
                  Forgot your password?
                </button>
                <div className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <button
                    onClick={() => setMode('register')}
                    className="font-medium text-indigo-600 hover:text-indigo-500"
                    style={{ color: primaryColor }}
                  >
                    Sign up
                  </button>
                </div>
              </div>
            )}

            {mode === 'register' && (
              <div className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => setMode('login')}
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                  style={{ color: primaryColor }}
                >
                  Sign in
                </button>
              </div>
            )}

            {mode === 'forgot-password' && (
              <button
                onClick={() => setMode('login')}
                className="text-sm text-indigo-600 hover:text-indigo-500"
                style={{ color: primaryColor }}
              >
                Back to sign in
              </button>
            )}

            {mode === 'verify-email' && (
              <div className="space-y-2">
                <div className="text-sm text-gray-600">
                  Didn't receive the email?{' '}
                  <button
                    onClick={handleResendVerification}
                    disabled={isLoading}
                    className="font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
                    style={{ color: primaryColor }}
                  >
                    Resend
                  </button>
                </div>
                <button
                  onClick={() => setMode('login')}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                  style={{ color: primaryColor }}
                >
                  Back to sign in
                </button>
              </div>
            )}
          </div>

          {/* Benefits (register mode) */}
          {mode === 'register' && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Account Benefits:</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Download history tracking</li>
                <li>• Higher quality video options</li>
                <li>• Batch download capabilities</li>
                <li>• Ad-free experience</li>
                <li>• Priority support</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal; 